import { PORTFOLIO_BENCHMARK_CACHE_TTL_MS } from '@/constants/common.constant'
import { environment } from '@/environment'
import axiosInstance from '@utils/interceptor'

// A report library entry ('YYYY-MM-DD'). endDate is null when the API omits it; callers that need
// a real begin+end range (attribution reportId resolution) filter those out.
export interface ReportLibraryRange {
  beginDate: string
  endDate: string | null
}

interface PortfolioResponse {
  name: string
  description: string | null
  date: string | null
  type: string | null
  currency: string | null
  benchmarkName: string | null
  benchmarkDescription: string | null
  dates: string[]
  numOfSecurities: number | null
}

interface Benchmark {
  vendor: string
  description: string
  apiName: string
  userNumber: number
}

// Format market value as a compact number (e.g., 100M, 1.5B).
export const formatMarketValue = (numValue: number): string => {
  if (numValue >= 1000000000) return `${(numValue / 1000000000).toFixed(2)}B`
  if (numValue >= 1000000) return `${(numValue / 1000000).toFixed(2)}M`
  if (numValue >= 1000) return `${(numValue / 1000).toFixed(2)}K`
  return numValue.toFixed(0)
}

class RADashboardService {
  private baseUrl = environment.apiBaseUrl
  private static cachedData: {
    portfolios: { name: string }[]
    benchmarks: Benchmark[]
    fullPortfolioData: any[]
  } | null = null
  private static cachedAt = 0
  private static dataPromise: Promise<{
    portfolios: { name: string }[]
    benchmarks: Benchmark[]
    fullPortfolioData: any[]
  }> | null = null
  private static benchmarkCache: Benchmark[] | null = null
  // Bumped by clearCache() so a fetch already in flight when a clear happens
  // can tell its result is stale by the time it resolves (see getPortfolioData).
  private static generation = 0

  // Invalidates the cached portfolio/benchmark data so the next
  // getPortfolioData() call refetches instead of serving a stale in-memory
  // snapshot for up to PORTFOLIO_BENCHMARK_CACHE_TTL_MS.
  clearCache(): void {
    RADashboardService.cachedData = null
    RADashboardService.cachedAt = 0
    RADashboardService.dataPromise = null
    RADashboardService.generation += 1
  }

  async getPortfolioData(): Promise<{
    portfolios: { name: string }[]
    benchmarks: Benchmark[]
    fullPortfolioData: any[]
  }> {
    // Return cached data if it's still fresh (see PORTFOLIO_BENCHMARK_CACHE_TTL_MS)
    const isStale = Date.now() - RADashboardService.cachedAt > PORTFOLIO_BENCHMARK_CACHE_TTL_MS
    if (RADashboardService.cachedData && !isStale) {
      return RADashboardService.cachedData
    }

    // If API call is already in progress, return the existing promise
    if (RADashboardService.dataPromise) {
      return RADashboardService.dataPromise
    }

    // Captured so a clearCache() that runs while this fetch is in flight can
    // be detected below — otherwise this fetch's result would land after the
    // clear and silently re-populate/re-stamp the cache it was meant to wipe.
    const generation = RADashboardService.generation

    // Create and store the promise
    RADashboardService.dataPromise = this.fetchData()

    try {
      const result = await RADashboardService.dataPromise
      if (RADashboardService.generation === generation) {
        RADashboardService.cachedData = result
        RADashboardService.cachedAt = Date.now()
      }
      return result
    } catch (error) {
      console.error('Error fetching portfolio/benchmark data:', error)
      // Don't cache a failure as if it were fresh data — clearing (rather
      // than stamping) cachedData/cachedAt guarantees the next call retries
      // instead of serving/re-stamping an empty snapshot as fresh for the
      // next PORTFOLIO_BENCHMARK_CACHE_TTL_MS.
      if (RADashboardService.generation === generation) {
        RADashboardService.cachedData = null
        RADashboardService.cachedAt = 0
      }
      return { portfolios: [], benchmarks: [], fullPortfolioData: [] }
    } finally {
      if (RADashboardService.generation === generation) {
        RADashboardService.dataPromise = null
      }
    }
  }

  private async fetchData(): Promise<{
    portfolios: { name: string }[]
    benchmarks: Benchmark[]
    fullPortfolioData: any[]
  }> {
    // Let a failure here propagate to getPortfolioData()'s catch rather than
    // swallowing it — that's the only place that can tell success from
    // failure and decide whether it's safe to cache the result. That
    // includes a benchmark-only failure: getBenchmarkList() below has no
    // callers besides this one, so it's free to throw instead of resolving
    // to [] — otherwise a benchmark failure would slip through as a
    // "successful" (but incomplete) result and get cached as fresh.
    const [portfolioResponse, benchmarkResponse] = await Promise.all([
      axiosInstance.get('/reportDashboard/api/v1/getPortfolioNames'),
      this.getBenchmarkList(),
    ])

    const portfolioData: PortfolioResponse[] = portfolioResponse.data

    if (!Array.isArray(portfolioData)) {
      // Same reasoning — this discards the benchmarks fetched above, so it
      // must not be treated as a cacheable success either.
      throw new Error('Unexpected response shape from getPortfolioNames')
    }

    const uniquePortfolios = Array.from(new Map(portfolioData.map((p) => [p.name, { name: p.name }])).values())

    // Add "Select Portfolio" as the first option
    const portfoliosWithSelect = [{ name: 'Select Portfolio' }, ...uniquePortfolios]

    return { portfolios: portfoliosWithSelect, benchmarks: benchmarkResponse, fullPortfolioData: portfolioData }
  }

  async getBenchmarkList(): Promise<Benchmark[]> {
    const response = await axiosInstance.get('/reportDashboard/api/v1/getBenchmarkList')
    return response.data
  }

  async getPortfolioDates(portfolioName: string): Promise<{ dates: string[] }> {
    try {
      const response = await axiosInstance.get(
        `/reportDashboard/api/v1/getPortfolioDates?portfolioName=${encodeURIComponent(portfolioName)}`,
      )
      return response.data
    } catch (error) {
      console.error('Error fetching portfolio dates:', error)
      return { dates: [] }
    }
  }

  async getMarketValue(
    portfolioName: string,
    benchmarkName: string,
    date: string,
  ): Promise<{
    marketValueNumber: number | null
    marketValue: string | null
    baseCurrency: string | null
    assetClass: string | null
    loadedPortfolio: string | null
    loadedDate: string | null
  }> {
    try {
      // Convert date from yyyy-mm-dd to yyyymmdd format
      const formattedDate = date.replace(/-/g, '')

      const response = await axiosInstance.get('/reportDashboard/api/v1/getPortfolioBenchmarkMarketInfo', {
        params: {
          portfolioName: portfolioName,
          benchmarkName: benchmarkName,
          date: formattedDate,
        },
      })

      // Extract market value and asset class from the response
      const data = response.data
      const marketValueField = data.port?.mkval
      const assetClassField = data.port?.assetClass
      const baseCurrency: string | null = data.port?.base_currency
        ? String(data.port.base_currency).toUpperCase()
        : null
      let marketValueNum = null
      let formattedMarketValue = null
      if (marketValueField !== null && marketValueField !== undefined) {
        const numValue = Number(marketValueField)
        if (!isNaN(numValue)) {
          marketValueNum = numValue
          formattedMarketValue = formatMarketValue(numValue)
        }
      }

      const portDateNum = data.port?.date
      const loadedDate = portDateNum
        ? `${String(portDateNum).slice(0, 4)}-${String(portDateNum).slice(4, 6)}-${String(portDateNum).slice(6, 8)}`
        : null

      return {
        marketValueNumber: marketValueNum,
        marketValue: formattedMarketValue,
        baseCurrency,
        assetClass: assetClassField || null,
        loadedPortfolio: data.port?.name ?? null,
        loadedDate,
      }
    } catch (error) {
      console.error('Error fetching market value:', error)
      return {
        marketValue: null,
        baseCurrency: null,
        assetClass: null,
        marketValueNumber: null,
        loadedPortfolio: null,
        loadedDate: null,
      }
    }
  }

  async getReportLibraryDates(
    reportType: string = 'GFR',
    portfolioName?: string,
    benchmarkName?: string,
    templateName: string = 'POSTGRES',
    hedgingPolicy?: string,
    matrixName?: string,
  ): Promise<ReportLibraryRange[]> {
    try {
      const params: any = {
        reportType: reportType,
        templateName: templateName,
      }

      if (portfolioName) {
        params.portfolioName = portfolioName
      }

      if (benchmarkName) {
        params.benchmarkName = benchmarkName
      }

      if (matrixName) {
        params.matrixName = matrixName
      }

      if (hedgingPolicy) {
        params.hedgingPolicy = hedgingPolicy
      }

      const response = await axiosInstance.get('/reportDashboard/api/v1/getReportLibraryDates', {
        params: params,
      })
      // API returns [{ beginDate: 20260508, endDate: 20260508 }, ...] with integer yyyymmdd dates.
      const raw: Array<{ beginDate?: number; endDate?: number }> = response.data || []
      const fmt = (n?: number): string | null => {
        const s = String(n ?? '')
        return s.length === 8 ? `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}` : null
      }
      return raw
        .map((item) => {
          const beginDate = fmt(item.beginDate)
          if (!beginDate) return null
          // Keep the row on a parseable begin date (drives the picker for GFR + GFPA); leave endDate
          // null when the API omits it rather than synthesizing one, so attribution can skip it.
          return { beginDate, endDate: fmt(item.endDate) }
        })
        .filter((row): row is ReportLibraryRange => row !== null)
    } catch (error) {
      console.error('Error fetching report library dates:', error)
      return []
    }
  }

  async checkReportExists(
    reportType: string,
    portfolioName: string,
    benchmarkName: string,
    beginDate: string,
    templateName: string = 'POSTGRES',
    endDate?: string,
    hedgingPolicy?: string,
    matrixName?: string,
  ): Promise<{ reportId: number; exists: boolean; message: string }> {
    try {
      // Convert date from yyyy-mm-dd to yyyymmdd format
      const formattedBeginDate = beginDate.replace(/-/g, '')
      const formattedEndDate = endDate ? endDate.replace(/-/g, '') : undefined

      const params: any = {
        reportType: reportType,
        portfolioName: portfolioName,
        benchmarkName: benchmarkName,
        beginDate: formattedBeginDate,
        templateName: templateName,
        hedgingPolicy,
        matrixName,
      }

      if (formattedEndDate) {
        params.endDate = formattedEndDate
      }

      const response = await axiosInstance.get('/reportDashboard/api/v1/checkReportExists', {
        params: params,
      })
      return response.data
    } catch (error) {
      console.error('Error checking report existence:', error)
      return { reportId: 0, exists: false, message: 'Error checking report' }
    }
  }

  async getReportStatus(uuid: string): Promise<{ uuid: string; status: string; report_id: number }> {
    const response = await axiosInstance.get('/reportDashboard/api/v1/getReportStatus', {
      params: { uuid },
    })
    return response.data
  }

  async runReport(
    reportType: string,
    portfolioName: string,
    benchmarkName: string,
    templateName: string,
    date: number,
    hedged: string,
    hedgedValue: number,
    matrixName: string,
    matrixDateSelect: number,
  ): Promise<{ clientId: number; message: string; requestUUID: string; reportId: number; alreadyExists: boolean }> {
    try {
      const queryParams = new URLSearchParams({
        reportType: encodeURIComponent(reportType),
        portfolioName: encodeURIComponent(portfolioName),
        benchmarkName: encodeURIComponent(benchmarkName),
        templateName: encodeURIComponent(templateName),
        date: date.toString(),
        hedged: encodeURIComponent(hedged),
        hedgedValue: hedgedValue.toString(),
        matrixName: encodeURIComponent(matrixName),
        matrixDateSelect: matrixDateSelect.toString(),
      })

      const response = await axiosInstance.post(`/reportDashboard/api/v1/runReport?${queryParams.toString()}`)
      return response.data
    } catch (error) {
      console.error('Error running report:', error)
      throw error
    }
  }

  // Attribution (GFPA) reports run per-day over a range and don't use the hedging/matrix
  // params GFR needs — they instead need date/endDate as a (previous weekday, target day) pair,
  // since the backend treats date as exclusive and endDate as the actual day being generated.
  async runAttributionReport(
    reportType: string,
    portfolioName: string,
    benchmarkName: string,
    templateName: string,
    date: number,
    endDate: number,
  ): Promise<{ clientId: number; message: string; requestUUID: string; reportId: number; alreadyExists: boolean }> {
    try {
      const queryParams = new URLSearchParams({
        reportType: encodeURIComponent(reportType),
        portfolioName: encodeURIComponent(portfolioName),
        benchmarkName: encodeURIComponent(benchmarkName),
        templateName: encodeURIComponent(templateName),
        date: date.toString(),
        endDate: endDate.toString(),
      })

      const response = await axiosInstance.post(`/reportDashboard/api/v1/runReport?${queryParams.toString()}`)
      return response.data
    } catch (error) {
      console.error('Error running attribution report:', error)
      throw error
    }
  }

  async saveDashboardState(state: {
    selectedPortfolio: string
    selectedBenchmark: string
    selectedDate: string
    activeTab: number
  }): Promise<void> {
    // Save user's dashboard preferences
    localStorage.setItem('ra-dashboard-state', JSON.stringify(state))
  }

  async loadDashboardState(): Promise<{
    selectedPortfolio: string
    selectedBenchmark: string
    selectedDate: string
    activeTab: number
  } | null> {
    try {
      const savedState = localStorage.getItem('ra-dashboard-state')
      return savedState ? JSON.parse(savedState) : null
    } catch (error) {
      return null
    }
  }
}

export const raDashboardService = new RADashboardService()
